import React, { PropsWithChildren } from "react";
import ReactModal from "react-modal";
import { fontObj } from "../../styles/mixins";
import { mql } from "../../styles/breakpoints";
import close from "../../images/close.svg";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { ButtonWithIcon } from "../ButtonWithIcon";

ReactModal.setAppElement("#___gatsby");

export const Content = styled.div`
  input::placeholder,
  textarea::placeholder {
    font-size: 14px;
    line-height: 16px;
    color: #A9A8B7;
    white-space: pre-wrap;
    z-index: 1;

    ${ mql.md } {
      font-size: 16px;
      line-height: 16px;
    }
  }
`;

const Header = styled.div<any>`
  position: relative;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${ (props: any) => props.isTitle ? "8px" : 0 };
`;

const Subtitle = styled.p`
  max-width: 530px;
  margin: 0 0 24px;
  font-size: 14px;
  line-height: 21px;
  color: rgb(33 33 33 / 60%);

  ${ mql.md } {
    margin: 0 0 32px;
    font-size: 18px;
    line-height: 27px;
  }
`;

const Close = styled(ButtonWithIcon)<any>`
  position: ${ (props: any) => props.closeIconProps ? "absolute" : "relative" };
  top: ${ (props: any) => props.closeIconProps?.top ?? "-8px" };
  right: ${ (props: any) => props.closeIconProps?.right ?? "-4px" };
  margin-left: 10px;
  cursor: pointer;
  z-index: 1;

  ${ mql.md } {
    top: ${ (props: any) => props.closeIconProps?.top ?? "-4px" };
  }
`;

const H2 = styled.h2`
  ${ fontObj };
  margin: 0;
  font-size: 24px;
  line-height: 28px;

  ${ mql.md } {
    font-size: 28px;
    line-height: 34px;
  }
`;

interface CloseIconProps {
  top: string;
  right: string;
  bgColor?: string;
}

export type IModalProps = ReactModal.Props & {
  content: React.ReactNode;
  withoutPadding?: boolean;
  title?: string;
  subtitle?: string;
  closeIconProps?: CloseIconProps;
}

export function Modal({
  title,
  subtitle,
  content,
  portalClassName,
  closeIconProps,
  withoutPadding,
  ...props
}: PropsWithChildren<IModalProps>) {
  return <ClassNames>
    { ({ css, cx }) => (
      <ReactModal
        onAfterOpen={ () => document.body.style.overflow = "hidden" }
        onAfterClose={ () => document.body.style.overflow = "auto" }

        overlayClassName={ {
          base: "overlay-base",
          afterOpen: "overlay-after",
          beforeClose: "overlay-before",
        } }
        className={ {
          base: "content-base",
          afterOpen: "content-after",
          beforeClose: "content-before",
        } }
        closeTimeoutMS={ 400 }
        preventScroll={ true }
        portalClassName={ cx(css`
          .overlay-base {
            display: flex;
            flex-direction: column;
            align-items: center;
            position: fixed;
            padding: 16px;
            top: 0;
            right: 0;
            left: 0;
            bottom: 0;
            cursor: pointer;
            overflow-y: scroll;
            z-index: 1;
            transition: all .4s ease-out;

            ${ mql.md } {
              justify-content: center;
            }
          }

          .overlay-after {
            background-color: rgb(31 31 41 / 40%);

            ${ mql.md } {
              background-color: rgb(31 31 41 / 20%);
            }
          }

          .overlay-before {
            background: transparent;
          }

          .content-base {
            max-width: 600px;
            padding: ${ withoutPadding ? 0 : "20px" };
            flex-shrink: 0;
            border-radius: 24px;
            overflow: ${ closeIconProps ? "visible" : "hidden" };
            box-sizing: content-box;
            background-color: white;
            transition: all .4s ease-out;
            border: none;
            outline: none;
            cursor: auto;

            &:focus {
              border: none;
              outline: none;
            }

            ${ mql.md } {
              padding: ${ withoutPadding ? 0 : "32px" };
              border-radius: 32px;
            }
          }

          .content-after {

          }

          .content-before {
            display: none;
          }
        `, portalClassName) }
        { ...props }
      >
        <Header isTitle={ !!title }>
          { title ? <H2>{ title }</H2> : <div/> }
          <Close onClick={ props.onRequestClose }
                 icon={ close }
                 closeIconProps={ closeIconProps }
                 bgColor={ closeIconProps?.bgColor }/>
        </Header>
        {/*@ts-ignore */ }
        <Content>
          { subtitle && <Subtitle>{ subtitle }</Subtitle> }
          { content }
        </Content>
      </ReactModal>
    ) }
  </ClassNames>;
}